import styles from "./UnitDetail.module.scss";
import { getUnitSummaryById } from "Explore/Services/Unit";
import useExploreInfo from "Hooks/useExploreInfo";
import { useEffect, useRef, useState } from "react";
import BasicInformation from "./Components/BasicInformation";
import UnitDetailKeywordCloud from "./Components/KeywordCloud";
// import UnitSummary from "./Components/UnitSummary";
import UnitDetailScholarAndPeers from "./Components/UnitScholarAndPeers";
import UnitNotableConcepts from "./Components/UnitNotableConcepts";
import UnitCollaboratorGraph from "./Components/UnitCollaboratorGraph";
import UnitInternationalCollaborators from "./Components/UnitInternationalCollaborators";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { ImageCheckType, ImageType } from "Explore/Config/ImageType";
import { checkImageUrl } from "Explore/Utils/CheckImageUrl";
import { setLandscapeBackground } from "Redux/Slices/Common";
import { useDispatch } from "react-redux";
import { Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const UnitDetail = () => {
  const { unit_id } = useExploreInfo();
  const { isMobile } = useDeviceCheck();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ data: {}, isLoading: false });
  const keywordRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setSummary({ data: {}, isLoading: true });
    getUnitSummaryById(unit_id).then((res) => {
      setSummary({ data: res, isLoading: false });
      if (res?.explore_slug && res.explore_slug !== unit_id) {
        navigate(`/explore/unit/${res.explore_slug}`, { replace: true });
      }
      // Fetch landscape background from the institution
      if (res?.institution_id) {
        checkImageUrl(
          `https://media.academic.online/institutions_images/${res.institution_id}/${ImageType.LANDSCAPE}.png`,
          (img) => dispatch(setLandscapeBackground(img)),
          ImageCheckType.INSTITUTION_LANDSCAPE
        );
      }
    });

    return () => {
      dispatch(setLandscapeBackground(null));
    };
  }, [unit_id]);

  const onKeywordClickCallback = () => {
    keywordRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const unitName = summary.data?.name;
  const institutionName = summary.data?.institution_name;
  
  const generateTitle = () => {
    if (!summary.data?.display_name) return "Academic Unit Profile | Academic Online";
    const name = summary.data.display_name;
    const institution = summary.data.institution_name;
    const suffix = " | Academic Online";
    const maxLength = 60;

    let titleFull = `${name}, ${institution} — Faculty & Research`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleInst = institution ? `${name} at ${institution}` : name;
    if ((titleInst + suffix).length <= maxLength) return titleInst + suffix;

    if ((name + suffix).length <= maxLength) return name + suffix;

    return `${name.substring(0, 40)}...${suffix}`;
  };
  
  const generateDescription = () => {
    const defaultDesc = "View detailed academic unit profile including research keywords, notable concepts, collaborator network, and affiliated scholars.";
    const unitName = summary.data?.display_name || summary.data?.name;
    if (!unitName) return defaultDesc;

    const institutionName = summary.data?.institution_name;
    const maxLength = 160;

    let descFull = institutionName ? `Explore ${unitName} at ${institutionName} including research keywords, concepts, collaborator network, and scholars.` : `Explore ${unitName}'s profile including research keywords, notable concepts, collaborator network, and affiliated scholars.`;
    if (descFull.length <= maxLength) return descFull;

    let descShort = `Explore ${unitName}'s profile including research keywords, concepts, collaborator network, and scholars.`;
    if (descShort.length <= maxLength) return descShort;

    return `${descShort.substring(0, 157)}...`;
  };

  return (
    <div className={styles["unit-detail"]}>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <div className={styles["section--hero"]}>
        <BasicInformation
          unit_id={unit_id || summary.data?.id}
          institution_id={summary.data?.institution_id}
          summary={summary}
        />
      </div>
      <section className={styles["section"]}>
        <Container maxWidth="lg">
          <UnitNotableConcepts onKeywordClickCallback={onKeywordClickCallback} />
        </Container>
      </section>
      <section className={`${styles["section"]} ${styles["section--alt"]}`}>
        <Container maxWidth="lg">
          <div ref={keywordRef}>
            <UnitDetailKeywordCloud />
          </div>
        </Container>
      </section>
      <section className={styles["section"]}>
        <Container maxWidth="lg">
          <UnitCollaboratorGraph />
        </Container>
      </section>
      <section className={`${styles["section"]} ${styles["section--alt"]}`}>
        <Container maxWidth="lg">
          <UnitDetailScholarAndPeers summary={summary.data} />
        </Container>
      </section>
    </div>
  );
};

export default UnitDetail;

